import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, KeyRound, Database, ArrowRightLeft, FileText,
  Shield, FlaskConical, MessageSquare, ClipboardList, BarChart3, Settings,
  GitBranch, PlusCircle, HelpCircle, ExternalLink, UsersRound, Sparkles } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { onCmdK } from "@/lib/keyboard";

const NAVIGATE_ITEMS = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { label: "Identity Providers", href: "/identity-providers", icon: KeyRound },
  { label: "Directory", href: "/directory", icon: UsersRound },
  { label: "Connectors", href: "/connectors", icon: Database },
  { label: "Pipelines", href: "/pipelines", icon: ArrowRightLeft },
  { label: "Data Catalog", href: "/data-catalog", icon: FileText },
  { label: "Policy Studio", href: "/policy-studio", icon: Shield },
  { label: "Access Simulator", href: "/access-simulator", icon: FlaskConical },
  { label: "Answer Synthesis", href: "/answer-synthesis", icon: MessageSquare },
  { label: "Audit Logs", href: "/audit-log", icon: ClipboardList },
  { label: "Usage & Billing", href: "/usage-billing", icon: BarChart3 },
  { label: "Relationships", href: "/relationships", icon: GitBranch },
  { label: "Settings", href: "/settings", icon: Settings },
];

const QUICK_ACTIONS = [
  { label: "New Policy", href: "/policy-studio", icon: PlusCircle },
  { label: "Add Connector", href: "/connectors", icon: Database },
  { label: "Run Simulator", href: "/access-simulator", icon: FlaskConical },
  { label: "View Audit Log", href: "/audit-log", icon: ClipboardList },
];

const HELP_ITEMS = [
  { label: "View Docs", href: "https://gateco.ai/docs", icon: HelpCircle, external: true },
  { label: "Contact Support", href: "mailto:support@gateco.ai", icon: ExternalLink, external: true },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    return onCmdK(() => setOpen((o) => !o));
  }, []);

  function run(href: string, external?: boolean) {
    setOpen(false);
    if (external) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      navigate(href);
    }
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigate">
          {NAVIGATE_ITEMS.map((item) => (
            <CommandItem key={item.href} onSelect={() => run(item.href)}>
              <item.icon />
              <span>{item.label}</span>
              <CommandShortcut>Go</CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Quick Actions">
          {QUICK_ACTIONS.map((item) => (
            <CommandItem key={item.label} onSelect={() => run(item.href)}>
              <item.icon />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Help">
          <CommandItem
            onSelect={() => {
              setOpen(false);
              window.dispatchEvent(new CustomEvent("gateco:open-ask-ai"));
            }}
          >
            <Sparkles />
            <span>Ask AI</span>
          </CommandItem>
          {HELP_ITEMS.map((item) => (
            <CommandItem key={item.label} onSelect={() => run(item.href, item.external)}>
              <item.icon />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
